import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Loader2, ShieldCheck, CheckCircle2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/apiError';
import OnboardingStepIndicator from './OnboardingStepIndicator';

// Phase 3 UX (Feb 2026): the previously separate Privacy+Terms step and the
// Digital Accountability Agreement step were combined into ONE required
// legal screen with a single "Agree and continue" action.
//
// The user-facing framing is role-specific:
//   • Manager → "Before you start" (Terms, Privacy, responsibilities)
//   • Worker  → "Your commitment to shifts"
//
// Underneath, we STILL fire three separate /legal/accept calls so the
// audit trail (version + timestamp per document) is preserved unchanged.
// If any of the three is already accepted on the user record, that call is
// skipped so returning users never re-consent.

const MANAGER_HIGHLIGHTS = [
  'You have read and accept the Terms of Use and Privacy Policy.',
  'You confirm your responsibilities when posting shifts — accurate details, timely responses and fair treatment of workers.',
  'You agree that shifts you post, till checks, applicants, approvals and communications are recorded as part of the operational history.',
];

const WORKER_COMMITMENTS = [
  'Attend accepted shifts.',
  'Arrive on time.',
  'Contact the manager promptly if there is a problem.',
  'Record attendance and shift information honestly.',
  'Understand that completed shifts, cancellations and attendance contribute to your reliability record.',
];

export default function LegalAcceptanceStep({ onDone }) {
  const { api, user, refreshUser } = useAuth();
  const isManager = user?.role === 'manager';
  const [versions, setVersions] = useState({ privacy: '1.0', terms: '1.0', accountability: '1.0' });
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/legal/versions');
        if (res?.data?.versions) setVersions((v) => ({ ...v, ...res.data.versions }));
      } catch { /* keep defaults */ }
    })();
  }, [api]);

  const needsPrivacy = !user?.privacyAcceptedVersion;
  const needsTerms = !user?.termsAcceptedVersion;
  const needsAccountability = user?.accountabilityAgreementAccepted !== true;

  const handleSubmit = async () => {
    if (!accepted) {
      toast.error('Please tick the box to confirm.');
      return;
    }
    setSubmitting(true);
    try {
      if (needsPrivacy) await api.post('/legal/accept', { documentType: 'privacy', version: versions.privacy });
      if (needsTerms) await api.post('/legal/accept', { documentType: 'terms', version: versions.terms });
      if (needsAccountability) await api.post('/legal/accept', { documentType: 'accountability', version: versions.accountability });
      await refreshUser();
      if (typeof onDone === 'function') onDone();
    } catch (err) {
      toast.error(formatApiError(err, 'We could not save your acceptance. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const heading = isManager ? 'Before you start' : 'Your commitment to shifts';
  const items = isManager ? MANAGER_HIGHLIGHTS : WORKER_COMMITMENTS;
  const accentColor = isManager ? '#0f1b3d' : '#DA291C';

  return (
    <div className="min-h-screen bg-[#F1F5F9] flex items-center justify-center p-4" data-testid="legal-acceptance-step">
      <Card className="w-full max-w-md border-0 shadow-lg">
        <CardContent className="p-8">
          {/* Step 2 in the 5-step onboarding sequence for BOTH roles. */}
          <OnboardingStepIndicator current={2} role={isManager ? 'manager' : 'worker'} />

          <div className="flex items-center gap-3 mb-2">
            <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: accentColor }}>
              <ShieldCheck className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-xl font-bold text-slate-900" data-testid="legal-heading">{heading}</h2>
          </div>
          <p className="text-base text-slate-600 mb-5">
            {isManager
              ? 'A quick summary of what you\'re agreeing to when you post shifts on ShiftCover365.'
              : 'What we ask from every worker who accepts shifts through ShiftCover365.'}
          </p>

          <ul className="space-y-3 mb-6" data-testid="legal-bullet-list">
            {items.map((item, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0" style={{ color: accentColor }} />
                <span className="text-base text-slate-700 leading-relaxed" data-testid={`legal-bullet-${idx}`}>{item}</span>
              </li>
            ))}
          </ul>

          <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 mb-5">
            <p className="text-sm font-semibold text-slate-700 mb-2">Full documents</p>
            <div className="flex flex-col gap-1.5">
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-sm text-[#4338CA] hover:underline inline-flex items-center gap-1" data-testid="legal-terms-link">
                Terms of Use (v{versions.terms}) <ExternalLink className="h-3 w-3" />
              </a>
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-sm text-[#4338CA] hover:underline inline-flex items-center gap-1" data-testid="legal-privacy-link">
                Privacy Policy (v{versions.privacy}) <ExternalLink className="h-3 w-3" />
              </a>
              <a href="/accountability-agreement" target="_blank" rel="noopener noreferrer" className="text-sm text-[#4338CA] hover:underline inline-flex items-center gap-1" data-testid="legal-accountability-link">
                Accountability Agreement (v{versions.accountability}) <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer mb-5" data-testid="legal-accept-row">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-1 h-5 w-5 rounded border-slate-300 shrink-0"
              style={{ accentColor }}
              data-testid="legal-accept-checkbox"
            />
            <span className="text-base text-slate-700 leading-relaxed">
              {isManager
                ? 'I confirm I have read and agree to the Terms of Use, Privacy Policy and Accountability Agreement.'
                : 'I understand and commit to the responsibilities above, and I agree to the Terms of Use and Privacy Policy.'}
            </span>
          </label>

          <Button
            onClick={handleSubmit}
            disabled={submitting || !accepted}
            className="w-full h-12 rounded-full text-white text-base font-semibold disabled:opacity-60"
            style={{ backgroundColor: accentColor }}
            data-testid="legal-accept-submit-btn"
          >
            {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</>) : 'Agree and continue'}
          </Button>
          <p className="text-xs text-slate-400 text-center mt-3">
            Your acceptance, document versions and timestamp are recorded for audit.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
